import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Alert, MenuItem, TextField } from '@mui/material';
import { Compare as CompareIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const thStyle = { border: '1px solid #ddd', padding: '6px 8px', textAlign: 'center', fontWeight: 700, whiteSpace: 'nowrap', fontSize: '11px' };
const tdStyle = { border: '1px solid #e0e0e0', padding: '4px 8px', whiteSpace: 'nowrap', fontSize: '11px' };
const vendorColors = ['#e8f5e9', '#e3f2fd', '#fff3e0', '#fce4ec', '#f3e5f5', '#e0f7fa'];

const VendorComparisonTab = ({ selectedSiteId, sites, userRole }) => {
  const navigate = useNavigate();
  const site = sites.find(s => s.id === selectedSiteId);
  const siteName = site ? `${site.siteName} — ${site.location}` : '';

  const [comparisons, setComparisons] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchComparisons = useCallback(async () => {
    if (!selectedSiteId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/vendor-comparisons/site/${selectedSiteId}`, authHeaders());
      const list = res.data.data.comparisons;
      setComparisons(list);
      if (list.length > 0) {
        setSelectedId(list[0].id);
        fetchDetail(list[0].id);
      } else {
        setComparison(null);
      }
    } catch (e) { setError('Failed to load comparisons'); }
    finally { setLoading(false); }
  }, [selectedSiteId]);

  const fetchDetail = async (id) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/vendor-comparisons/${id}`, authHeaders());
      setComparison(res.data.data.comparison);
    } catch (e) { setError('Failed to load comparison'); }
  };

  useEffect(() => { fetchComparisons(); }, [fetchComparisons]);

  if (!selectedSiteId) return <Alert severity="info">Please select a site to view vendor comparison.</Alert>;
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  if (comparisons.length === 0) return (
    <Box sx={{ textAlign: 'center', py: 6 }}>
      <CompareIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>No Vendor Comparisons Yet</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload vendor quotations for {siteName} to compare rates
      </Typography>
      {(userRole === 'admin' || userRole === 'manager') && (
        <Button variant="contained" startIcon={<OpenInNewIcon />}
          onClick={() => navigate(`/vendor-comparison?siteId=${selectedSiteId}&siteName=${encodeURIComponent(siteName)}`)}>
          Go to Vendor Comparison
        </Button>
      )}
    </Box>
  );

  // Build mini pivot
  const quotes = comparison?.quotes || [];
  const materials = [...new Set(quotes.map(q => q.material))].sort();
  const vendors = [...new Set(quotes.map(q => q.vendorName))].sort();

  const pivotMap = {};
  quotes.forEach(q => {
    if (!pivotMap[q.material]) pivotMap[q.material] = {};
    if (!pivotMap[q.material][q.vendorName]) pivotMap[q.material][q.vendorName] = [];
    pivotMap[q.material][q.vendorName].push(q);
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight={700}>Vendor Comparison</Typography>
          {comparisons.length > 1 && (
            <TextField select size="small" value={selectedId}
              onChange={(e) => { setSelectedId(e.target.value); fetchDetail(e.target.value); }}
              sx={{ minWidth: 120 }}>
              {comparisons.map(c => <MenuItem key={c.id} value={c.id}>{c.versionLabel}</MenuItem>)}
            </TextField>
          )}
          {comparisons.length === 1 && (
            <Typography variant="body2" color="text.secondary">{comparisons[0].versionLabel}</Typography>
          )}
        </Box>
        <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
          onClick={() => navigate(`/vendor-comparison?siteId=${selectedSiteId}&siteName=${encodeURIComponent(siteName)}`)}>
          Full View
        </Button>
      </Box>

      {/* Mini Pivot Table */}
      {comparison && (
        <Box sx={{ overflow: 'auto', maxHeight: '50vh', border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <table style={{ borderCollapse: 'collapse', minWidth: 'max-content', width: '100%', fontSize: '12px' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, background: '#1a237e', color: 'white', minWidth: 140, position: 'sticky', left: 0, zIndex: 2 }}>Material</th>
                <th style={{ ...thStyle, background: '#1a237e', color: 'white', minWidth: 60 }}>Unit</th>
                {vendors.map((vendor, vIdx) => (
                  <th key={vendor} style={{ ...thStyle, background: vendorColors[vIdx % vendorColors.length], color: '#1a237e', minWidth: 100 }}>
                    {vendor}
                  </th>
                ))}
                <th style={{ ...thStyle, background: '#ffd700', color: '#1a237e', minWidth: 80 }}>Best Rate</th>
              </tr>
            </thead>
            <tbody>
              {materials.map((material, mIdx) => {
                const firstQuote = quotes.find(q => q.material === material);
                const rates = vendors.map(vendor => {
                  const q = (pivotMap[material]?.[vendor] || [])[0];
                  return q ? parseFloat(q.rate) : null;
                }).filter(r => r !== null);
                const bestRate = rates.length > 0 ? Math.min(...rates) : null;

                return (
                  <tr key={material} style={{ background: mIdx % 2 === 0 ? 'white' : '#f9f9f9' }}>
                    <td style={{ ...tdStyle, background: '#e8eaf6', fontWeight: 600, position: 'sticky', left: 0, zIndex: 1 }}>{material}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{firstQuote?.unit || ''}</td>
                    {vendors.map((vendor, vIdx) => {
                      const vendorQuotes = pivotMap[material]?.[vendor] || [];
                      const rate = vendorQuotes.length > 0 ? parseFloat(vendorQuotes[0].rate) : null;
                      const isBest = rate !== null && rate === bestRate;
                      return (
                        <td key={vendor} style={{
                          ...tdStyle,
                          background: isBest ? '#c8e6c9' : rate ? vendorColors[vIdx % vendorColors.length] : 'white',
                          textAlign: 'right',
                          fontWeight: isBest ? 700 : rate ? 500 : 400,
                          color: isBest ? '#2e7d32' : rate ? '#1a237e' : '#ccc'
                        }}>
                          {rate ? rate.toLocaleString('en-IN') : '—'}
                        </td>
                      );
                    })}
                    <td style={{ ...tdStyle, background: '#fff9c4', textAlign: 'right', fontWeight: 700, color: '#e65100' }}>
                      {bestRate ? bestRate.toLocaleString('en-IN') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};

export default VendorComparisonTab;